import { CMSLandingPage } from '@/features/cms/components/CMS/CMSLandingPage'
import { RouteName } from '@shared/constants/routes'
import { createServerSidePropsHandler } from '@/features/shared/serverUtils/ssrHelpers/createServerSidePropsHandler'
import { withHeaderLayout } from '@/features/shared/components/Headers/Layouts/HeaderLayout'
import { inferZipAndStoreForSlugifiedRoute } from '@/features/shared/serverUtils/inferZipAndStoreForSlugifiedRoute'
import {
  fetchCMSDataForBotSSR,
  type GetCMSPageDataParams,
} from '@/features/cms/services/CMS/serverUtils'
import { getIsBot } from '@/features/shared/serverUtils/getIsBot'
import { getZipForBot } from '@/features/shared/serverUtils/guest'
import { shoppingStoresQueryOptions } from '@/features/shop/services/ShoppingStore/queries'
import { toKebabCase } from '@shared/utils/url'

type Props = {
  retailerAgnosticSlug?: string
}

const getRetailerNameForSSR = async ({ queryClient, ssrContext }: GetCMSPageDataParams) => {
  const storeSlug = ssrContext.query.store_slug

  if (typeof storeSlug !== 'string') return undefined

  // Bots must ALWAYS use the designated bot zip so the retailer's store is
  // guaranteed to be available in the results for the given slug.
  const zip = getZipForBot(storeSlug)

  const shoppingStores = await queryClient.query(
    shoppingStoresQueryOptions({ address_zip_code: zip }),
  )
  // we are fetching here to get the store name which is not the same as the slug.
  //  The slug might be like total-wine-more but the name which comes back is Total Wine & More.
  // This is fed into the CMS call as an input variable to populate SEO metadata for the page
  return (
    shoppingStores.delivery_stores.find((store) => toKebabCase(store.name) === storeSlug)?.name ??
    storeSlug
  )
}

export const getServerSideProps = createServerSidePropsHandler<Props>(
  RouteName.RETAILER_LANDING_PAGE,
  async (props) => {
    const redirect = await inferZipAndStoreForSlugifiedRoute(props)
    if ('redirect' in redirect) {
      return redirect
    }
    const { params } = props.ssrContext.params ?? {}

    if (Array.isArray(params)) {
      const retailerAgnosticSlug = `shop/landing-page/${params.join('/')}`

      if (getIsBot(props.ssrContext.req.headers)) {
        const retailerName = await getRetailerNameForSSR(props)

        await fetchCMSDataForBotSSR({
          pageParams: props,
          slugOverride: retailerAgnosticSlug,
          inputVariables: { retailer_name: retailerName },
        })
        return {
          props: {
            retailerAgnosticSlug,
            isBot: true,
          },
        }
      }

      return {
        props: {
          retailerAgnosticSlug,
        },
      }
    }
    // given that this route is defined with a spread params path. It should be impossible for params to not be an array, but we still have to return an empty object here for TypeScript purposes.
    return {}
  },
)

// landing pages are all CMS driven
export default withHeaderLayout(CMSLandingPage, { suppressFooter: true })
